/**
 * Date difference — pure date math.
 *
 * Takes ISO date strings ("YYYY-MM-DD"); the "countdown from today" mode passes
 * today's ISO from the component, so the lib stays deterministic (no
 * `Date.now()`). Integer Y/M/D + `Date.UTC` → no timezone/DST drift.
 */

const DAY_MS = 86_400_000;

export interface DateParts {
  y: number;
  m: number;
  d: number;
}

export function parseISO(iso: string): DateParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > daysInMonth(y, m)) return null;
  return { y, m, d };
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Weekdays (Mon–Fri) in the half-open interval [fromUTC, toUTC). */
function countWeekdays(fromUTC: number, toUTC: number): number {
  const days = Math.round((toUTC - fromUTC) / DAY_MS);
  if (days <= 0) return 0;
  const startDow = new Date(fromUTC).getUTCDay();
  const fullWeeks = Math.floor(days / 7);
  let weekdays = fullWeeks * 5;
  const rem = days % 7;
  for (let i = 0; i < rem; i++) {
    const dow = (startDow + i) % 7;
    if (dow !== 0 && dow !== 6) weekdays++;
  }
  return weekdays;
}

export interface DateDiffResult {
  /** Whether `to` is after, before or the same as `from`. */
  direction: "future" | "past" | "same";
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalWeeks: number;
  totalMonths: number;
  weekdays: number;
  weekendDays: number;
}

/** Difference between two dates (absolute breakdown + direction of `to`). */
export function dateDiff(
  fromISO: string,
  toISO: string,
): DateDiffResult | null {
  const f = parseISO(fromISO);
  const t = parseISO(toISO);
  if (!f || !t) return null;

  const fUTC = Date.UTC(f.y, f.m - 1, f.d);
  const tUTC = Date.UTC(t.y, t.m - 1, t.d);
  const direction = tUTC > fUTC ? "future" : tUTC < fUTC ? "past" : "same";

  const lo = fUTC <= tUTC ? f : t;
  const hi = fUTC <= tUTC ? t : f;
  const loUTC = Math.min(fUTC, tUTC);
  const hiUTC = Math.max(fUTC, tUTC);

  let years = hi.y - lo.y;
  let months = hi.m - lo.m;
  let days = hi.d - lo.d;
  if (days < 0) {
    months -= 1;
    let pm = hi.m - 1;
    let py = hi.y;
    if (pm === 0) {
      pm = 12;
      py -= 1;
    }
    days += daysInMonth(py, pm);
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const totalDays = Math.round((hiUTC - loUTC) / DAY_MS);
  const weekdays = countWeekdays(loUTC, hiUTC);

  return {
    direction,
    years,
    months,
    days,
    totalDays,
    totalWeeks: Math.floor(totalDays / 7),
    totalMonths: years * 12 + months,
    weekdays,
    weekendDays: totalDays - weekdays,
  };
}
